import {
  StatusBreakdownChart,
  type StatusSegment,
} from "../StatusBreakdownChart";
import type { AiProviderModelCostRow } from "./aiProviderMonitoring";
import { formatEstimatedCostUsd } from "./aiUsage";
import {
  AI_USAGE_ACTIVITIES,
  AI_USAGE_ACTIVITY_LABELS,
} from "./aiUsageActivities";
import { SPACE_COLORS } from "./monitoringPanelLayout";

type Props = {
  modelRows: readonly AiProviderModelCostRow[];
};

export function AiProviderModelSpendChart({ modelRows }: Props) {
  const segments: StatusSegment[] = modelRows.map((row, i) => {
    const activityCost = AI_USAGE_ACTIVITIES.reduce(
      (sum, activity) => sum + (row.byActivity[activity].estimatedCostUsd ?? 0),
      0
    );
    // Spend from sources that aren't build/run/monitor (personal chat, value
    // estimates); surfaced so the activity rows reconcile with Total.
    const otherCost = (row.estimatedCostUsd ?? 0) - activityCost;
    return {
      key: row.model,
      count: row.estimatedCostUsd ?? 0,
      color: SPACE_COLORS[i % SPACE_COLORS.length]!,
      label: row.model,
      details: [
        { label: "Total", value: formatEstimatedCostUsd(row.estimatedCostUsd) },
        ...AI_USAGE_ACTIVITIES.map((activity) => ({
          label: AI_USAGE_ACTIVITY_LABELS[activity],
          value: formatEstimatedCostUsd(
            row.byActivity[activity].estimatedCostUsd || null
          ),
        })),
        ...(otherCost > 0.005
          ? [{ label: "Other", value: formatEstimatedCostUsd(otherCost) }]
          : []),
        { label: "Calls", value: row.requestCount.toLocaleString("en-US") },
      ],
    };
  });

  const total = modelRows.reduce(
    (sum, row) => sum + (row.estimatedCostUsd ?? 0),
    0
  );

  return (
    <StatusBreakdownChart
      title="Spend by model"
      total={total}
      segments={segments}
      formatValue={formatEstimatedCostUsd}
      embedded
      wrapLegend
      showLegendValue={false}
      placeholder={modelRows.length === 0}
    />
  );
}
